import { useId } from "react";
import { Box } from "@mui/material";
import {
  controlSx,
  getStatus,
  inputSx,
  labelSx,
  messageSx,
} from "../_shared/fieldStyles";

/**
 * TextInput
 *
 * Props:
 * - label: text above the field
 * - error: true, or a message string shown under the field (red state)
 * - success: green state
 * - disabled
 * - id: input id (generated when omitted)
 * - sx: extra styles for the whole field, merged last
 * - anything else (value, onChange, placeholder, type, ...) goes to the input
 */
const TextInput = ({
  label,
  error,
  success = false,
  disabled = false,
  id,
  sx: sxOverride = {},
  ...rest
}) => {
  const generatedId = useId();
  const inputId = id || generatedId;
  const status = getStatus({ error, success, disabled });
  const message = typeof error === "string" ? error : null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        ...sxOverride,
      }}
    >
      {label && (
        <Box component="label" htmlFor={inputId} sx={labelSx(disabled)}>
          {label}
        </Box>
      )}
      <Box sx={controlSx(status)}>
        <Box
          component="input"
          id={inputId}
          disabled={disabled}
          aria-invalid={status === "error" || undefined}
          aria-describedby={message ? `${inputId}-error` : undefined}
          {...rest}
          sx={inputSx(disabled)}
        />
      </Box>
      {message && (
        <Box id={`${inputId}-error`} role="alert" sx={messageSx}>
          {message}
        </Box>
      )}
    </Box>
  );
};

export default TextInput;
